/**
 * Referral system for Ingrediable.
 *
 * How it works:
 * 1. Every user gets a unique referral code (stored in profiles.referral_code)
 * 2. Users share their link: https://ingrediable.com/?ref=CODE
 * 3. When a new user signs up via a referral link, both get a free month of Pro
 * 4. Credits are tracked in profiles.referral_credits
 *
 * The referral code is captured on the landing page and stored in localStorage.
 * On signup, the code is sent to the backend to link the referral.
 */

const REFERRAL_STORAGE_KEY = "ingrediable-referral-code";

/**
 * Capture referral code from URL parameters.
 * Call this on app initialization.
 */
export function captureReferralCode(): string | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const code = params.get("ref");

  if (code) {
    try {
      localStorage.setItem(REFERRAL_STORAGE_KEY, code);
    } catch {}
    // Clean up URL
    const url = new URL(window.location.href);
    url.searchParams.delete("ref");
    window.history.replaceState({}, "", url.toString());
    return code;
  }

  return getStoredReferralCode();
}

/**
 * Get the stored referral code (if any).
 */
export function getStoredReferralCode(): string | null {
  try {
    return localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear the stored referral code after it's been used.
 */
export function clearReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {}
}

/**
 * Generate a shareable referral link.
 */
export function getReferralLink(code: string): string {
  const base = typeof window !== "undefined" ? window.location.origin : "https://ingrediable.com";
  return `${base}/?ref=${code}`;
}

/**
 * Copy referral link to clipboard.
 */
export async function copyReferralLink(code: string): Promise<boolean> {
  const link = getReferralLink(code);
  try {
    await navigator.clipboard.writeText(link);
    return true;
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement("textarea");
    textarea.value = link;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

/**
 * Apply referral on signup.
 * Call this after a new user is created.
 */
export async function applyReferral(userId: string): Promise<void> {
  const code = getStoredReferralCode();
  if (!code) return;

  try {
    const { supabase } = await import("./supabase-client").catch(() => ({ supabase: null }));
    if (!supabase) return;

    // Link the referral
    await (supabase as any).from("profiles").update({
      referred_by: code,
    }).eq("id", userId);

    // Credit the referrer
    await (supabase as any).rpc("increment_referral_credits", { referral_code_input: code });

    clearReferralCode();
  } catch (err) {
    console.error("Failed to apply referral:", err);
  }
}
